import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import ShareBar from '../components/ShareBar';
import Disclaimer from '../components/Disclaimer';

const API_BASE = process.env.REACT_APP_API_URL || 'https://knowfi-api.onrender.com';
const NEXT_HALVING = new Date('2028-04-20T00:00:00Z');

// ── Section definitions ─────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'bitcoin',
    label: 'Bitcoin 🟠',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.08)',
    border: 'rgba(249,115,22,0.25)',
    tagline: 'The original. 21 million. No CEO.',
    description: 'Bitcoin is the first cryptocurrency, created in 2009 by Satoshi Nakamoto. It runs on a decentralized network with no central authority — no company controls it. There will only ever be 21 million bitcoins, written into the code. Every ~4 years, the amount of new bitcoin created gets cut in half (the "halving"). Bitcoin is often called digital gold — a scarce, portable store of value that anyone in the world can hold.',
    coins: ['bitcoin'],
    shareUrl: 'https://knowfi.io/crypto-corner?tab=bitcoin',
    blogLinks: [
      { label: 'Bitcoin Explained', slug: 'bitcoin-explained' },
      { label: 'What Is a Bitcoin Halving?', slug: 'what-is-a-bitcoin-halving' },
      { label: 'Bitcoin vs. Gold', slug: 'bitcoin-vs-gold' },
      { label: 'What Is a Crypto Wallet?', slug: 'what-is-a-crypto-wallet' },
    ],
  },
  {
    id: 'defi',
    label: 'DeFi ⚡',
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.08)',
    border: 'rgba(167,139,250,0.25)',
    tagline: 'Banking without banks.',
    description: 'Decentralized Finance (DeFi) puts financial services — lending, borrowing, trading, earning yield — on blockchain networks run by code instead of companies. There\'s no customer service department, no business hours, and no one who can freeze your account. Ethereum hosts most DeFi activity, but Solana, Avalanche, and others compete. Anyone with a crypto wallet can access DeFi globally, 24/7.',
    coins: ['ethereum', 'solana', 'avalanche-2'],
    shareUrl: 'https://knowfi.io/crypto-corner?tab=defi',
    blogLinks: [
      { label: 'What Is DeFi?', slug: 'what-is-defi' },
      { label: 'What Is a Smart Contract?', slug: 'what-is-a-smart-contract' },
      { label: 'What Is Staking in Crypto?', slug: 'what-is-staking-in-crypto' },
      { label: 'What Is Ethereum?', slug: 'what-is-ethereum' },
    ],
  },
  {
    id: 'tokenization',
    label: 'Tokenization 🔗',
    color: '#38bdf8',
    bg: 'rgba(56,189,248,0.08)',
    border: 'rgba(56,189,248,0.25)',
    tagline: 'Real-world assets on-chain.',
    description: 'Tokenization converts ownership of real-world assets — Treasury bonds, real estate, private credit, commodities — into digital tokens on a blockchain. BlackRock, Franklin Templeton, JPMorgan, and Fidelity have all launched tokenized products. The market has grown from near-zero to over $15 billion in tokenized assets since 2022. Tokenized assets settle in minutes, trade 24/7, and can be split into fractions.',
    coins: ['chainlink', 'ondo-finance', 'centrifuge'],
    shareText: (btc, fg) =>
      `BlackRock tokenized $2B+ in assets. JPMorgan settled $1T+ on-chain. The old financial system is moving to blockchain.${fg ? ` Fear & Greed: ${fg.value}.` : ''} — via KnowFi`,
    shareUrl: 'https://knowfi.io/crypto-corner?tab=tokenization',
    blogLinks: [
      { label: 'What Is Tokenization of RWAs?', slug: 'what-is-tokenization-of-real-world-assets' },
      { label: 'What Are Tokenized Stocks?', slug: 'what-are-tokenized-stocks' },
      { label: 'What Is the BUIDL Fund?', slug: 'what-is-the-buidl-fund' },
      { label: 'DeFi vs. Traditional Finance', slug: 'defi-vs-traditional-finance' },
    ],
  },
];

// ── Coin metadata ────────────────────────────────────────────────────────────
const COIN_META = {
  bitcoin:        { symbol: 'BTC',   name: 'Bitcoin',      note: 'Created 2009. Fixed supply of 21 million. Largest crypto by market cap.' },
  ethereum:       { symbol: 'ETH',   name: 'Ethereum',     note: 'Primary blockchain for DeFi apps and smart contracts. Launched 2015.' },
  solana:         { symbol: 'SOL',   name: 'Solana',       note: 'High-speed, low-cost blockchain. ~65,000 transactions per second.' },
  ripple:         { symbol: 'XRP',   name: 'XRP',          note: 'Designed for bank-to-bank cross-border payments. Fast, low cost.' },
  cardano:        { symbol: 'ADA',   name: 'Cardano',      note: 'Proof-of-stake blockchain built on peer-reviewed academic research.' },
  'avalanche-2':  { symbol: 'AVAX',  name: 'Avalanche',    note: 'Smart contract platform with ~1 second finality. Used for institutional DeFi.' },
  polkadot:       { symbol: 'DOT',   name: 'Polkadot',     note: 'Connects separate blockchains so they can share data and transactions.' },
  chainlink:      { symbol: 'LINK',  name: 'Chainlink',    note: 'Dominant oracle network — feeds real-world data (prices, rates) into blockchains. Critical for tokenized assets.' },
  'ondo-finance': { symbol: 'ONDO',  name: 'Ondo Finance', note: 'Issues tokenized US Treasuries and money market funds accessible on-chain. $600M+ TVL.' },
  centrifuge:     { symbol: 'CFG',   name: 'Centrifuge',   note: 'Brings real-world assets (invoices, loans, real estate) onto blockchain for DeFi collateral. $500M+ financed.' },
  polymesh:       { symbol: 'POLYX', name: 'Polymesh',     note: 'Blockchain built specifically for regulated security tokens with built-in compliance.' },
};

// ── Tokenization data ────────────────────────────────────────────────────────
const MARKET_SNAPSHOTS = [
  {
    label: 'Total Tokenized RWAs', value: '$15B+', color: '#38bdf8',
    detail: 'Total real-world assets currently represented as tokens on blockchains worldwide. Includes US Treasuries, private credit, real estate, and commodities. Grew from near-zero in 2021.',
    linkedCoins: ['chainlink', 'ondo-finance', 'centrifuge'],
  },
  {
    label: 'US Treasuries On-Chain', value: '$7B+', color: '#818cf8',
    detail: 'US government bonds tokenized and living on blockchains. Investors can hold T-bill exposure 24/7, earn yield on-chain, and use them as DeFi collateral — without going through a broker.',
    linkedCoins: ['ondo-finance'],
  },
  {
    label: 'Private Credit Tokenized', value: '$8B+', color: '#4ade80',
    detail: 'Business loans, real estate loans, and invoices tokenized for faster settlement and fractional investment. Centrifuge is the primary infrastructure bringing these to DeFi.',
    linkedCoins: ['centrifuge'],
  },
  {
    label: 'JPMorgan Repo Settled', value: '$1T+', color: '#fbbf24',
    detail: 'JPMorgan\'s Onyx network has processed over $1 trillion in intraday repo transactions on blockchain — proving institutional-scale tokenized finance is already here.',
    linkedCoins: ['polymesh'],
  },
  {
    label: 'BlackRock BUIDL AUM', value: '$2B+', color: '#38bdf8',
    detail: 'BlackRock\'s BUIDL fund — a tokenized money market fund on Ethereum — crossed $2 billion in assets under management. It\'s the largest tokenized fund by AUM.',
    linkedCoins: ['ondo-finance'],
  },
  {
    label: 'Institutions Active', value: '50+', color: '#f472b6',
    detail: 'Over 50 major financial institutions have launched or piloted tokenized asset products, including BlackRock, JPMorgan, Franklin Templeton, Fidelity, Goldman Sachs, KKR, and Hamilton Lane.',
    linkedCoins: ['chainlink', 'polymesh'],
  },
];

const ASSET_CLASSES = [
  {
    label: 'US Treasuries', amount: '$7B+', color: '#38bdf8',
    note: 'T-bills and money market funds tokenized for 24/7 access and DeFi collateral.',
    detail: 'Tokenized US Treasuries let anyone hold government bond exposure on-chain, earn yield, and use them as collateral in DeFi protocols — 24/7, with no broker. BlackRock BUIDL and Franklin Templeton BENJI are the flagship products.',
    linkedCoins: ['ondo-finance'],
  },
  {
    label: 'Private Credit', amount: '$8B+', color: '#818cf8',
    note: 'Loans and invoices tokenized for faster settlement and fractional investment.',
    detail: 'Private credit (business loans, real estate mortgages, invoice financing) traditionally requires millions to access. Tokenization breaks it into fractions anyone can hold — and cuts settlement from weeks to hours.',
    linkedCoins: ['centrifuge'],
  },
  {
    label: 'Real Estate', amount: '$300M+', color: '#4ade80',
    note: 'Property deeds and REIT shares tokenized for fractional ownership.',
    detail: 'Tokenized real estate converts property ownership into digital tokens. Investors can buy a fraction of a building, receive proportional rental income, and trade their stake 24/7 — something impossible with traditional REITs.',
    linkedCoins: ['centrifuge', 'polymesh'],
  },
  {
    label: 'Commodities', amount: '$1B+', color: '#fbbf24',
    note: 'Gold, silver, and oil backed by physical holdings, tradeable on-chain 24/7.',
    detail: 'Tokenized gold (e.g., PAX Gold, Tether Gold) is backed 1:1 by physical gold held in vaults. Each token = 1 troy ounce. Tradeable globally, fractionable, and settles in seconds instead of days.',
    linkedCoins: ['chainlink'],
  },
];

const TOKENIZATION_MILESTONES = [
  { institution: 'BlackRock',          product: 'BUIDL Fund',          asset: 'Money Market',      amount: '$2B+ AUM',        year: '2024' },
  { institution: 'Franklin Templeton', product: 'BENJI',               asset: 'US Treasuries',     amount: '$400M+',          year: '2021' },
  { institution: 'JPMorgan',           product: 'Onyx / TCN',          asset: 'Repo Transactions', amount: '$1T+ settled',    year: '2020' },
  { institution: 'Ondo Finance',       product: 'OUSG / USDY',         asset: 'US Treasuries',     amount: '$600M+ TVL',      year: '2023' },
  { institution: 'Centrifuge',         product: 'Tinlake',             asset: 'Private Credit',    amount: '$500M+ financed', year: '2021' },
  { institution: 'Fidelity',           product: 'FOBXX',               asset: 'Money Market',      amount: 'Institutional',   year: '2023' },
  { institution: 'KKR',                product: 'Health Care Fund',    asset: 'Private Equity',    amount: 'First tokenized PE', year: '2022' },
  { institution: 'Hamilton Lane',      product: 'Senior Credit Fund',  asset: 'Private Credit',    amount: 'First tokenized VC', year: '2022' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1000) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  if (n >= 1)    return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}
function chgColor(n) { return (!n && n !== 0) ? '#64748b' : n >= 0 ? '#4ade80' : '#f87171'; }
function fgColor(val) {
  if (val >= 75) return '#4ade80';
  if (val >= 55) return '#86efac';
  if (val >= 45) return '#fde68a';
  if (val >= 25) return '#fca5a5';
  return '#f87171';
}
function timeAgo(d) {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 1)    return 'just now';
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}
function halvingCountdown() {
  const diff = NEXT_HALVING - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };
  return {
    days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours:   Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
  };
}

// ── Sub-components ───────────────────────────────────────────────────────────
function PriceCard({ coinId, coin, accent }) {
  const meta    = COIN_META[coinId] || { symbol: coinId.toUpperCase(), name: coinId };
  const price   = coin?.price;
  const chg     = coin?.change24h ?? coin?.changePercent;
  const hasP    = price !== null && price !== undefined && !isNaN(price);
  const chgNum  = chg !== null && chg !== undefined && !isNaN(parseFloat(chg)) ? parseFloat(chg) : null;
  return (
    <div style={{ background: 'rgba(15,23,42,0.9)', border: `1px solid ${accent}28`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: meta.note ? 10 : 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#f1f5f9' }}>{meta.symbol}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{meta.name}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: hasP ? '#f1f5f9' : '#475569' }}>
            {hasP ? fmt(price) : <span style={{ fontSize: 12, color: '#475569' }}>Loading…</span>}
          </div>
          {chgNum !== null && (
            <div style={{ fontSize: 12, fontWeight: 700, color: chgColor(chgNum), marginTop: 2 }}>
              {chgNum >= 0 ? '+' : ''}{chgNum.toFixed(2)}%
              <span style={{ fontSize: 10, fontWeight: 400, color: '#475569' }}> 24h</span>
            </div>
          )}
        </div>
      </div>
      {meta.note && (
        <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.65, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8 }}>
          {meta.note}
        </div>
      )}
    </div>
  );
}

function NewsCard({ item }) {
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer"
      style={{ display: 'block', background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(51,65,85,0.4)', borderRadius: 10, padding: '12px 14px', textDecoration: 'none' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', lineHeight: 1.55, marginBottom: 5 }}>{item.title}</div>
      <div style={{ fontSize: 11, color: '#475569' }}>
        <span style={{ color: '#64748b', fontWeight: 600 }}>{item.source}</span>
        {item.pubDate && <span> · {timeAgo(item.pubDate)}</span>}
      </div>
    </a>
  );
}

// Clickable bubble that expands inline detail + linked coins
function ClickableBubble({ label, value, color, detail, linkedCoins = [], coinMeta = {} }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: open ? `${color}18` : `${color}0d`,
          border: `1px solid ${open ? color : color + '28'}`,
          borderRadius: open ? '10px 10px 0 0' : 10,
          padding: '12px 16px', cursor: 'pointer', textAlign: 'center',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 900, color, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 10, color: color + 'aa', marginTop: 3 }}>{open ? '▲ less' : '▼ tap for details'}</div>
      </button>
      {open && (
        <div style={{ background: `${color}08`, border: `1px solid ${color}28`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '12px 16px' }}>
          <p style={{ margin: '0 0 10px', color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>{detail}</p>
          {linkedCoins.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Linked tokens:</span>
              {linkedCoins.map(id => {
                const m = coinMeta[id];
                return m ? (
                  <span key={id} style={{ fontSize: 11, background: `${color}12`, border: `1px solid ${color}22`, borderRadius: 6, padding: '2px 8px', color, fontWeight: 700 }}>
                    {m.symbol} — {m.name}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function CryptoCorner() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?tab= query param is the single source of truth — survives Facebook/Twitter link sharing
  const tabFromParams = () => {
    const id = searchParams.get('tab')?.toLowerCase();
    return SECTIONS.find(s => s.id === id) ? id : 'bitcoin';
  };

  const [activeTab, setActiveTab]     = useState(tabFromParams);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [halvingTick, setHalvingTick] = useState(halvingCountdown());
  const [editorialArticles, setEditorialArticles] = useState([]);

  // Sync active tab when URL params change
  useEffect(() => {
    setActiveTab(tabFromParams());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Switch tab: update ?tab= param (replaces history entry so back button still works)
  const switchTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const fetchData = useCallback(async () => {
    try {
      const [cornerRes, editRes] = await Promise.all([
        fetch(`${API_BASE}/api/crypto-corner`),
        fetch(`${API_BASE}/api/editorial`),
      ]);
      if (!cornerRes.ok) throw new Error(`HTTP ${cornerRes.status}`);
      const json = await cornerRes.json();
      setData(json);
      setLastRefresh(new Date());
      setError(null);
      if (editRes.ok) {
        const editJson = await editRes.json();
        setEditorialArticles(editJson.articles || []);
      }
    } catch (e) {
      setError('Unable to load live data — retrying shortly.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const dataIv    = setInterval(fetchData, 120000);
    const halvingIv = setInterval(() => setHalvingTick(halvingCountdown()), 60000);
    return () => { clearInterval(dataIv); clearInterval(halvingIv); };
  }, [fetchData]);

  const section = SECTIONS.find(s => s.id === activeTab);

  // Match editorial articles to crypto tab
  const TAB_KEYWORDS = {
    bitcoin: ['bitcoin', 'btc', 'satoshi', 'halving', 'lightning network'],
    defi: ['defi', 'decentralized finance', 'ethereum', 'eth', 'uniswap', 'aave', 'yield', 'liquidity', 'smart contract', 'protocol', 'dao', 'solana', 'sol'],
    tokenization: ['tokeniz', 'rwa', 'real-world asset', 'on-chain', 'onchain', 'blackrock', 'jpmorgan', 'stablecoin', 'digital asset', 'blockchain finance', 'franklin templeton', 'crypto etf', 'bitcoin etf'],
  };
  const getMatchedArticle = (tabId) => {
    const keywords = TAB_KEYWORDS[tabId] || [];
    return editorialArticles.find(a => {
      const text = ((a.headline || '') + ' ' + (a.hook || '') + ' ' + (a.category || '')).toLowerCase();
      return keywords.some(kw => text.includes(kw));
    }) || null;
  };

  // Build priceMap: indexed by id + symbol (upper + lower)
  const priceMap = {};
  (data?.topCoins || []).forEach(c => {
    if (c.id)     priceMap[c.id]                   = c;
    if (c.symbol) priceMap[c.symbol.toUpperCase()] = c;
    if (c.symbol) priceMap[c.symbol.toLowerCase()] = c;
  });
  (data?.tokenizationTokens || []).forEach(t => {
    if (t.id     && !priceMap[t.id])                   priceMap[t.id]                   = t;
    if (t.symbol && !priceMap[t.symbol.toUpperCase()]) priceMap[t.symbol.toUpperCase()] = t;
  });

  const btc               = priceMap['bitcoin'];
  const fearGreed         = data?.fearGreed;
  const btcDominance      = data?.btcDominance;
  const defiTvl           = data?.defiTvl;
  const sectionNews       = data?.sectionNews?.[activeTab] || [];
  const sectionHeadlines  = data?.sectionHeadlines || {};

  // Build share text: headline (if available) + price/stat hook + KnowFi attribution
  function buildShareText(sectionId) {
    // Priority: top RSS headline → matched editorial article → section-specific fallback
    const hl = sectionHeadlines[sectionId];
    if (hl?.title) return hl.title.trim();
    const matched = getMatchedArticle(sectionId);
    if (matched?.headline) return matched.headline.trim();
    const fallbacks = {
      bitcoin: 'Bitcoin, explained without the jargon — KnowFi',
      defi: 'DeFi explained without the jargon — KnowFi',
      tokenization: 'Tokenization explained without the jargon — KnowFi',
    };
    return fallbacks[sectionId] || 'Crypto explained without the jargon — KnowFi';
  }

  const infoCard = (color) => ({
    background: `${color}0a`, border: `1px solid ${color}22`, borderRadius: 12, padding: '16px 18px',
  });
  const sLabel = (color) => ({
    fontSize: 11, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, display: 'block',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0a0f1e', color: '#e2e8f0', fontFamily: "'Inter',sans-serif" }}>
      <SEO
        title="Crypto Corner — Bitcoin, DeFi & Tokenization | KnowFi"
        description="Live crypto prices, breaking headlines, and plain-English explanations across Bitcoin, DeFi, and Tokenization. Free. Updated every 2 minutes."
        canonical="https://knowfi.io/crypto-corner"
      />

      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 16px 80px' }}>

        {/* ── HERO — minimal, no section stats ── */}
        <div style={{ padding: '52px 0 36px', textAlign: 'center', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 700, height: 260, background: 'radial-gradient(ellipse, rgba(99,102,241,0.13) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-block', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.28)', color: '#818cf8', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 18 }}>
              Free · Live · Updates every 2 min
            </div>
            <h1 style={{ fontSize: 'clamp(26px,6vw,42px)', fontWeight: 900, margin: '0 0 12px', background: 'linear-gradient(135deg, #e2e8f0 0%, #818cf8 55%, #38bdf8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.15 }}>
              Crypto Corner
            </h1>
            <p style={{ color: '#94a3b8', fontSize: 'clamp(14px,3vw,16px)', margin: '0 auto 8px', maxWidth: 480, lineHeight: 1.65 }}>
              Live prices, breaking headlines, and plain-English explanations — no wallet required.
            </p>
            {lastRefresh && (
              <div style={{ fontSize: 11, color: '#334155', marginTop: 6 }}>Last updated {timeAgo(lastRefresh.toISOString())}</div>
            )}
          </div>
        </div>

        {/* ── TABS ── */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 28, WebkitOverflowScrolling: 'touch' }}>
          {SECTIONS.map(sec => (
            <button key={sec.id} onClick={() => switchTab(sec.id)}
              style={{
                flexShrink: 0,
                background: activeTab === sec.id ? sec.bg : 'rgba(15,23,42,0.7)',
                border: `2px solid ${activeTab === sec.id ? sec.color : 'rgba(51,65,85,0.45)'}`,
                borderRadius: 12, padding: '11px 22px', cursor: 'pointer',
                color: activeTab === sec.id ? sec.color : '#64748b',
                fontWeight: 700, fontSize: 13,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                transition: 'all 0.18s', whiteSpace: 'nowrap',
              }}
            >
              <span>{sec.label}</span>
              <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>{sec.tagline}</span>
            </button>
          ))}
        </div>

        {loading && <div style={{ textAlign: 'center', color: '#475569', padding: '60px 0', fontSize: 15 }}>Loading live data…</div>}
        {error && !data && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ color: '#f87171', fontSize: 14, marginBottom: 12 }}>{error}</div>
            <button onClick={fetchData} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', color: '#818cf8', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Try again
            </button>
          </div>
        )}

        {data && section && (
          <>
            {/* ── SECTION HEADER ── */}
            <div style={{ background: section.bg, border: `1px solid ${section.border}`, borderRadius: 16, padding: '22px 24px', marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 900, color: section.color }}>{section.label}</h2>
              </div>
              <p style={{ margin: '0 0 14px', color: '#94a3b8', fontSize: 14, lineHeight: 1.8 }}>{section.description}</p>

              {/* Section-specific live stats strip */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {activeTab === 'bitcoin' && fearGreed && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(51,65,85,0.55)', borderRadius: 30, padding: '5px 12px' }}>
                    <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Fear &amp; Greed</span>
                    <div style={{ width: 50, height: 4, borderRadius: 2, background: 'rgba(51,65,85,0.7)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${fearGreed.value}%`, background: 'linear-gradient(90deg,#f87171,#fde68a,#4ade80)', borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 800, color: fgColor(fearGreed.value) }}>{fearGreed.value}</span>
                    <span style={{ fontSize: 11, color: fgColor(fearGreed.value) }}>{fearGreed.label}</span>
                  </div>
                )}
                {activeTab === 'bitcoin' && btcDominance && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 30, padding: '5px 12px' }}>
                    <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>BTC Dominance</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#f97316' }}>{btcDominance}%</span>
                  </div>
                )}
                {activeTab === 'defi' && fearGreed && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(51,65,85,0.55)', borderRadius: 30, padding: '5px 12px' }}>
                    <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Crypto Sentiment</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: fgColor(fearGreed.value) }}>{fearGreed.value} — {fearGreed.label}</span>
                  </div>
                )}
                {activeTab === 'defi' && defiTvl && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 30, padding: '5px 12px' }}>
                    <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Total DeFi TVL</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#a78bfa' }}>{fmt(defiTvl)}</span>
                  </div>
                )}
                {activeTab === 'tokenization' && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 30, padding: '5px 12px' }}>
                    <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Total Tokenized RWAs</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#38bdf8' }}>$15B+</span>
                  </div>
                )}
              </div>

              {/* Blog links */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {section.blogLinks.map(bl => (
                  <Link key={bl.slug} to={`/blog/${bl.slug}`}
                    style={{ fontSize: 12, color: section.color, fontWeight: 600, textDecoration: 'none', background: `${section.color}12`, border: `1px solid ${section.color}28`, borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                    {bl.label} →
                  </Link>
                ))}
              </div>
            </div>

            {/* ── TOP STORY BANNER — KnowFi article if matched, RSS fallback ── */}
            {(() => {
              const matched = getMatchedArticle(activeTab);
              const hl = sectionHeadlines[activeTab];
              if (!matched && !hl) return null;
              if (matched) {
                // KnowFi editorial article
                return (
                  <div style={{ background: `${section.color}0d`, border: `1px solid ${section.color}40`, borderRadius: 14, padding: '20px', marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: section.color, textTransform: 'uppercase', letterSpacing: 1, background: `${section.color}18`, borderRadius: 20, padding: '3px 10px' }}>✍️ KnowFi Analysis</span>
                      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>KnowFi Editorial Team</span>
                    </div>
                    <Link to={`/market-pulse?highlight=${encodeURIComponent(matched.headline || '')}`}
                      style={{ display: 'block', fontSize: 'clamp(15px,3.5vw,18px)', fontWeight: 800, color: '#f1f5f9', lineHeight: 1.4, textDecoration: 'none', marginBottom: 10 }}
                      onMouseEnter={e => e.currentTarget.style.color = section.color}
                      onMouseLeave={e => e.currentTarget.style.color = '#f1f5f9'}
                    >
                      {matched.headline}
                    </Link>
                    {matched.hook && (
                      <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, margin: '0 0 14px 0' }}>{matched.hook}</p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <Link to={`/market-pulse?highlight=${encodeURIComponent(matched.headline || '')}`}
                        style={{ fontSize: 12, color: section.color, fontWeight: 700, textDecoration: 'none', background: `${section.color}15`, border: `1px solid ${section.color}35`, borderRadius: 8, padding: '6px 14px' }}
                      >
                        Read Full Article →
                      </Link>
                      <span style={{ fontSize: 11, color: '#475569' }}>{matched.read_minutes || 4} min read · Updated every 2 hours</span>
                    </div>
                  </div>
                );
              }
              // RSS fallback
              return (
                <div style={{ background: `${section.color}0d`, border: `1px solid ${section.color}30`, borderRadius: 14, padding: '16px 20px', marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: section.color, textTransform: 'uppercase', letterSpacing: 1, background: `${section.color}18`, borderRadius: 20, padding: '3px 10px' }}>📰 Today's Top Story</span>
                    {hl.source && <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>{hl.source}</span>}
                  </div>
                  <a href={hl.link} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'block', fontSize: 'clamp(15px,3.5vw,17px)', fontWeight: 800, color: '#f1f5f9', lineHeight: 1.4, textDecoration: 'none', marginBottom: 10 }}
                    onMouseEnter={e => e.currentTarget.style.color = section.color}
                    onMouseLeave={e => e.currentTarget.style.color = '#f1f5f9'}
                  >
                    {hl.title}
                  </a>
                  <span style={{ fontSize: 11, color: '#334155' }}>Via {hl.source} — opens external source</span>
                </div>
              );
            })()}

            {/* ═══════════════════════════════════════════
                BITCOIN SECTION
            ════════════════════════════════════════════ */}
            {activeTab === 'bitcoin' && (
              <>
                {/* Halving countdown */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.22)', borderRadius: 14, padding: '14px 24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <span style={{ fontSize: 12, color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>⏳ Next Halving</span>
                    {[{ n: halvingTick.days, l: 'days' }, { n: halvingTick.hours, l: 'hours' }, { n: halvingTick.minutes, l: 'min' }].map(({ n, l }) => (
                      <div key={l} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#f97316', lineHeight: 1 }}>{n}</div>
                        <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                    <Link to="/blog/what-is-a-bitcoin-halving" style={{ fontSize: 11, color: '#f97316', fontWeight: 600, textDecoration: 'none', borderLeft: '1px solid rgba(249,115,22,0.25)', paddingLeft: 16 }}>
                      What is a halving? →
                    </Link>
                  </div>
                </div>

                {/* Bitcoin fundamentals */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Bitcoin Fundamentals</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, marginBottom: 28 }}>
                  <div style={infoCard('#f97316')}>
                    <span style={sLabel('#f97316')}>What Is the Halving?</span>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>
                      Every ~210,000 blocks (~4 years), the mining reward is cut in half — permanently reducing new supply. The 2024 halving cut rewards from 6.25 → 3.125 BTC per block. Next halving (~April 2028) drops it to 1.5625 BTC.
                    </p>
                    <Link to="/blog/what-is-a-bitcoin-halving" style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#f97316', fontWeight: 600, textDecoration: 'none' }}>Full explainer →</Link>
                  </div>
                  <div style={infoCard('#f97316')}>
                    <span style={sLabel('#f97316')}>The 21 Million Hard Cap</span>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>
                      Bitcoin's protocol permanently caps supply at 21 million coins — written in code, not policy. ~19.8 million have been mined. The remaining ~1.2 million will trickle out until ~2140 as block rewards approach zero.
                    </p>
                    <Link to="/blog/bitcoin-explained" style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#f97316', fontWeight: 600, textDecoration: 'none' }}>Bitcoin explained →</Link>
                  </div>
                  {btcDominance && (
                    <div style={infoCard('#f97316')}>
                      <span style={sLabel('#f97316')}>BTC Market Dominance</span>
                      <div style={{ fontSize: 34, fontWeight: 900, color: '#f97316', marginBottom: 8 }}>{btcDominance}%</div>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>
                        Bitcoin's share of the total crypto market cap. When dominance climbs, BTC is outperforming altcoins. When it falls, capital is rotating into ETH, DeFi, or speculative tokens.
                      </p>
                    </div>
                  )}
                </div>

                {/* Prices */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Main Player — Live Price</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10, marginBottom: 8 }}>
                  {section.coins.map(id => <PriceCard key={id} coinId={id} coin={priceMap[id]} accent={section.color} />)}
                </div>
                <p style={{ fontSize: 11, color: '#334155', marginBottom: 32, lineHeight: 1.6 }}>Prices for informational purposes only. Not investment advice.</p>
              </>
            )}

            {/* ═══════════════════════════════════════════
                DEFI SECTION
            ════════════════════════════════════════════ */}
            {activeTab === 'defi' && (
              <>
                {/* DeFi fundamentals */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>DeFi Fundamentals</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px,1fr))', gap: 14, marginBottom: 28 }}>
                  {defiTvl && (
                    <div style={infoCard('#a78bfa')}>
                      <span style={sLabel('#a78bfa')}>Total Value Locked (TVL)</span>
                      <div style={{ fontSize: 34, fontWeight: 900, color: '#a78bfa', marginBottom: 8 }}>{fmt(defiTvl)}</div>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>
                        TVL = total assets deposited inside all DeFi protocols globally. Think of it like total deposits at all DeFi "banks" combined. It's the primary measure of DeFi ecosystem health.
                      </p>
                    </div>
                  )}
                  <div style={infoCard('#a78bfa')}>
                    <span style={sLabel('#a78bfa')}>How DeFi Works</span>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.75 }}>
                      DeFi replaces bank employees with smart contracts — self-executing code on-chain. When you deposit funds, the contract automatically calculates interest, enforces rules, and handles withdrawals. No loan officer. No approval. No branch hours.
                    </p>
                    <Link to="/blog/what-is-defi" style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#a78bfa', fontWeight: 600, textDecoration: 'none' }}>What is DeFi? →</Link>
                  </div>
                  <div style={infoCard('#a78bfa')}>
                    <span style={sLabel('#a78bfa')}>Key DeFi Activities</span>
                    <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#94a3b8', fontSize: 13, lineHeight: 2.1 }}>
                      <li><strong style={{ color: '#e2e8f0' }}>Lending</strong> — Earn interest on deposits (no bank needed)</li>
                      <li><strong style={{ color: '#e2e8f0' }}>Borrowing</strong> — Use crypto as collateral for loans</li>
                      <li><strong style={{ color: '#e2e8f0' }}>Staking</strong> — Lock tokens to secure the network, earn rewards</li>
                      <li><strong style={{ color: '#e2e8f0' }}>Liquidity</strong> — Provide assets to trading pools, earn fees</li>
                    </ul>
                  </div>
                </div>

                {/* Prices */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Main Players — Live Prices</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10, marginBottom: 8 }}>
                  {section.coins.map(id => <PriceCard key={id} coinId={id} coin={priceMap[id]} accent={section.color} />)}
                </div>
                <p style={{ fontSize: 11, color: '#334155', marginBottom: 32, lineHeight: 1.6 }}>Prices for informational purposes only. Not investment advice.</p>
              </>
            )}

            {/* ═══════════════════════════════════════════
                TOKENIZATION SECTION
            ════════════════════════════════════════════ */}
            {activeTab === 'tokenization' && (
              <>
                {/* Market snapshot — clickable bubbles */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Market Snapshot — tap each for details</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 10, marginBottom: 28 }}>
                  {MARKET_SNAPSHOTS.map((ms, i) => (
                    <ClickableBubble key={i} {...ms} coinMeta={COIN_META} />
                  ))}
                </div>

                {/* What's being tokenized — clickable cards */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>What's Being Tokenized — tap each for details</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 10, marginBottom: 28 }}>
                  {ASSET_CLASSES.map((ac, i) => (
                    <ClickableBubble
                      key={i}
                      label={ac.label}
                      value={ac.amount}
                      color={ac.color}
                      detail={ac.detail}
                      linkedCoins={ac.linkedCoins}
                      coinMeta={COIN_META}
                    />
                  ))}
                </div>

                {/* Institutional milestones */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Institutional Milestones</div>
                <div style={{ background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
                  {TOKENIZATION_MILESTONES.map((m, i) => (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8,
                      padding: '12px 16px',
                      borderBottom: i < TOKENIZATION_MILESTONES.length - 1 ? '1px solid rgba(51,65,85,0.4)' : 'none',
                      alignItems: 'center',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{m.institution}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{m.product}</span>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{m.asset}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8', textAlign: 'right' }}>{m.amount}</span>
                    </div>
                  ))}
                </div>

                {/* Settlement comparison + Why it matters */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px,1fr))', gap: 14, marginBottom: 28 }}>
                  <div style={infoCard('#38bdf8')}>
                    <span style={sLabel('#38bdf8')}>Settlement Speed</span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                      <div style={{ textAlign: 'center', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '12px 8px' }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#f87171' }}>2–3 days</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Traditional stocks &amp; bonds</div>
                      </div>
                      <div style={{ textAlign: 'center', background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 8, padding: '12px 8px' }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#38bdf8' }}>&lt; 1 min</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Tokenized assets on-chain</div>
                      </div>
                    </div>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: 12, lineHeight: 1.65 }}>
                      Faster settlement means less counterparty risk, freed-up capital, and markets that never close.
                    </p>
                  </div>
                  <div style={infoCard('#38bdf8')}>
                    <span style={sLabel('#38bdf8')}>Why Tokenization Matters</span>
                    <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#94a3b8', fontSize: 13, lineHeight: 2 }}>
                      <li><strong style={{ color: '#e2e8f0' }}>Fractional ownership</strong> — own a slice of a $10M building</li>
                      <li><strong style={{ color: '#e2e8f0' }}>24/7 markets</strong> — no more waiting for Monday 9:30am</li>
                      <li><strong style={{ color: '#e2e8f0' }}>Instant settlement</strong> — no T+2, no clearing houses</li>
                      <li><strong style={{ color: '#e2e8f0' }}>Programmable rules</strong> — compliance built into the token</li>
                    </ul>
                    <Link to="/blog/what-is-tokenization-of-real-world-assets" style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#38bdf8', fontWeight: 600, textDecoration: 'none' }}>Full explainer →</Link>
                  </div>
                </div>

                {/* Live prices — BOTTOM for tokenization */}
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Main Players — Live Prices</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10, marginBottom: 8 }}>
                  {section.coins.map(id => <PriceCard key={id} coinId={id} coin={priceMap[id]} accent={section.color} />)}
                </div>
                <p style={{ fontSize: 11, color: '#334155', marginBottom: 32, lineHeight: 1.6 }}>Prices for informational purposes only. Not investment advice.</p>
              </>
            )}

            {/* ── NEWS (full-width, always at bottom) ── */}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                Latest Headlines
                {sectionNews.length > 0 && (
                  <span style={{ color: '#334155', fontSize: 10, fontWeight: 500, textTransform: 'none' }}>
                    {[...new Set(sectionNews.slice(0, 8).map(n => n.source))].join(' · ')}
                  </span>
                )}
              </div>
              {sectionNews.length > 0
                ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 8 }}>
                    {sectionNews.map((item, i) => <NewsCard key={i} item={item} />)}
                  </div>
                : <div style={{ color: '#475569', fontSize: 13, padding: '16px 0' }}>No headlines right now — check back shortly.</div>
              }
            </div>

            {/* Share bar — headline is the top story for the active tab */}
            <div style={{ margin: '36px 0 0', padding: '14px 18px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 12, textAlign: 'center' }}>
              <p style={{ color: '#818cf8', fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>📤 Share this page</p>
              <ShareBar
                text={buildShareText(activeTab)}
                url={section?.shareUrl || `https://knowfi.io/crypto-corner?tab=${activeTab}`}
              />
            </div>

            {/* Bottom links */}
            <div style={{ marginTop: 28, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/brief" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.22)', borderRadius: 10, padding: '9px 16px', color: '#818cf8', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                Today's KnowFi Brief
              </Link>
              <Link to="/blog?category=Technical Analysis" style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.22)', borderRadius: 10, padding: '9px 16px', color: '#a78bfa', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                How to Read Crypto Charts
              </Link>
              <Link to="/blog?category=Tokenization" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)', borderRadius: 10, padding: '9px 16px', color: '#38bdf8', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                Tokenization Guides
              </Link>
            </div>

            <Disclaimer />
          </>
        )}
      </div>
    </div>
  );
}
